import React, { useEffect, useMemo, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Trash2, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TextInput, SelectDropdown } from "@/components/formfields";
import type {
  ProfileSkillDetail,
  ProfileSkillBulkPayload,
} from "@/interface/profile";
import type { CategoryDetail, SkillDetail } from "@/interface/masterManagement";
import {
  useCreateProfileSkill,
  useUpdateProfileSkill,
  useDeleteProfileSkill,
  useSkillCategories,
  useSkillsByCategory,
} from "../hooks/useProfileSkills";
import { useProfileDetail } from "../hooks/useProfile";

const skillItemSchema = z.object({
  id: z.union([z.string(), z.number()]).optional(),
  skill: z.string().min(1, "Skill is required"),
  proficiency: z.string().optional(),
  experience_years: z.coerce.number().min(0).optional(),
});

const profileSkillFormSchema = z.object({
  categoryId: z.string().min(1, "Category is required"),
  skills: z.array(skillItemSchema).min(1, "At least one skill is required"),
});

export type ProfileSkillFormValues = z.infer<typeof profileSkillFormSchema>;

export interface ProfileSkillCategoryGroup {
  categoryId: number | string;
  categoryName?: string;
  skills: ProfileSkillDetail[];
}

interface ProfileSkillFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editCategoryData?: ProfileSkillCategoryGroup | null;
  profileId?: number | string;
}

const PROFICIENCY_OPTIONS = [
  { label: "Beginner", value: "beginner" },
  { label: "Intermediate", value: "intermediate" },
  { label: "Advanced", value: "advanced" },
  { label: "Expert", value: "expert" },
];

export const ProfileSkillFormModal: React.FC<ProfileSkillFormModalProps> = ({
  isOpen,
  onClose,
  editCategoryData,
  profileId,
}) => {
  const createMutation = useCreateProfileSkill();
  const updateMutation = useUpdateProfileSkill();
  const deleteMutation = useDeleteProfileSkill();
  const { data: profileResponse } = useProfileDetail();

  const selfId = profileResponse?.data?.id;
  const targetUserId = profileId ?? selfId;

  const isEditing = Boolean(editCategoryData);

  const [categorySearch, setCategorySearch] = useState<string>("");
  const [debouncedCategorySearch, setDebouncedCategorySearch] = useState<string>("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCategorySearch(categorySearch);
    }, 350);
    return () => clearTimeout(timer);
  }, [categorySearch]);

  const {
    data: categoryInfiniteData,
    isLoading: isCategoriesLoading,
    fetchNextPage: fetchNextCategories,
    hasNextPage: hasNextCategories,
    isFetchingNextPage: isFetchingNextCategories,
  } = useSkillCategories({
    page: 1,
    page_size: 10,
    search: debouncedCategorySearch || undefined,
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { isSubmitting },
  } = useForm<ProfileSkillFormValues>({
    resolver: zodResolver(profileSkillFormSchema),
    defaultValues: {
      categoryId: "",
      skills: [
        {
          skill: "",
          proficiency: "intermediate",
          experience_years: 0,
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "skills",
  });

  const selectedCategoryId = watch("categoryId");

  const [skillSearch, setSkillSearch] = useState<string>("");
  const [debouncedSkillSearch, setDebouncedSkillSearch] = useState<string>("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSkillSearch(skillSearch);
    }, 350);
    return () => clearTimeout(timer);
  }, [skillSearch]);

  useEffect(() => {
    setSkillSearch("");
    setDebouncedSkillSearch("");
  }, [selectedCategoryId]);

  const {
    data: skillInfiniteData,
    isLoading: isSkillsLoading,
    fetchNextPage: fetchNextSkills,
    hasNextPage: hasNextSkills,
    isFetchingNextPage: isFetchingNextSkills,
  } = useSkillsByCategory(selectedCategoryId, {
    page: 1,
    page_size: 10,
    search: debouncedSkillSearch || undefined,
  });

  const categoriesList: CategoryDetail[] = useMemo(() => {
    if (!categoryInfiniteData?.pages) return [];
    return categoryInfiniteData.pages.flatMap(
      (page) =>
        (Array.isArray(page?.data)
          ? page.data
          : page?.data?.results || []) as CategoryDetail[],
    );
  }, [categoryInfiniteData]);

  const categoryOptions = useMemo(() => {
    const list = [...categoriesList];
    if (
      editCategoryData?.categoryId &&
      !list.some(
        (cat) => String(cat.id) === String(editCategoryData.categoryId),
      )
    ) {
      list.unshift({
        id: Number(editCategoryData.categoryId),
        skill_category_name:
          editCategoryData.categoryName || String(editCategoryData.categoryId),
      } as CategoryDetail);
    }
    return list
      .map((cat: CategoryDetail) => ({
        label: cat.skill_category_name || "",
        value: String(cat.id),
      }))
      .filter((opt) => Boolean(opt.label));
  }, [categoriesList, editCategoryData]);

  const masterSkillsList: SkillDetail[] = useMemo(() => {
    if (!skillInfiniteData?.pages) return [];
    return skillInfiniteData.pages.flatMap(
      (page) =>
        (Array.isArray(page?.data)
          ? page.data
          : page?.data?.results || []) as SkillDetail[],
    );
  }, [skillInfiniteData]);

  const skillOptions = useMemo(() => {
    const list = [...masterSkillsList];
    if (editCategoryData?.skills && Array.isArray(editCategoryData.skills)) {
      editCategoryData.skills.forEach((sk) => {
        const skillId = sk.skill ?? sk.id;
        if (
          skillId &&
          !list.some((item) => String(item.id) === String(skillId))
        ) {
          list.unshift({
            id: Number(skillId),
            skill_name: sk.skill_name || `Skill #${skillId}`,
          } as SkillDetail);
        }
      });
    }
    return list
      .map((sk: SkillDetail) => ({
        label: sk.skill_name || "",
        value: String(sk.id),
      }))
      .filter((opt) => Boolean(opt.label));
  }, [masterSkillsList, editCategoryData]);

  useEffect(() => {
    if (isOpen) {
      setCategorySearch("");
      setDebouncedCategorySearch("");
      setSkillSearch("");
      setDebouncedSkillSearch("");
      if (editCategoryData) {
        const catId = String(editCategoryData.categoryId);
        const existingSkills = (editCategoryData.skills || []).map(
          (sk: ProfileSkillDetail) => ({
            id: sk.id,
            skill: String(sk.skill ?? ""),
            proficiency: sk.proficiency
              ? sk.proficiency.toLowerCase()
              : "intermediate",
            experience_years: sk.experience_years ?? 0,
          }),
        );

        reset({
          categoryId: catId,
          skills:
            existingSkills.length > 0
              ? existingSkills
              : [
                {
                  skill: "",
                  proficiency: "intermediate",
                  experience_years: 0,
                },
              ],
        });
      } else {
        reset({
          categoryId: "",
          skills: [
            {
              skill: "",
              proficiency: "intermediate",
              experience_years: 0,
            },
          ],
        });
      }
    }
  }, [isOpen, editCategoryData, reset]);

  const handleRemoveSkill = async (idx: number) => {
    const skillItem = getValues(`skills.${idx}`);
    const skillId = skillItem?.id;

    if (skillId) {
      try {
        await deleteMutation.mutateAsync(skillId);
        remove(idx);
      } catch {
        // Handled by query mutation
      }
    } else {
      remove(idx);
    }
  };

  const onSubmit = async (values: ProfileSkillFormValues) => {
    try {
      const validSkills = values.skills.filter((s) => Boolean(s.skill));

      if (validSkills.length === 0) return;

      const entries = validSkills.map((s) => ({
        ...(s.id ? { id: s.id } : {}),
        skill: Number(s.skill),
        proficiency: s.proficiency || "intermediate",
        experience_years: Number(s.experience_years) || 0,
        user_profile: targetUserId,
      }));

      const payload: ProfileSkillBulkPayload = {
        entries,
        user_profile: targetUserId,
      };

      if (isEditing) {
        await updateMutation.mutateAsync(payload);
      } else {
        await createMutation.mutateAsync(payload);
      }

      onClose();
    } catch {
      // Handled by query mutation
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800">
          <DialogTitle>
            {isEditing ? "Edit Skill Category" : "Add Skill Category"}
          </DialogTitle>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col flex-1 overflow-hidden"
        >
          <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50/50 dark:bg-slate-900/50">
            {/* Category SelectDropdown */}
            <div>
              <SelectDropdown
                control={control}
                name="categoryId"
                label="Category Name *"
                options={categoryOptions}
                isSearchable
                onSearch={(query) => setCategorySearch(query)}
                placeholder={
                  isCategoriesLoading
                    ? "Loading categories..."
                    : "Select Category"
                }
                onChange={(val) => {
                  setValue("categoryId", String(val));
                }}
                onMenuScrollToBottom={() => {
                  if (hasNextCategories && !isFetchingNextCategories) {
                    fetchNextCategories();
                  }
                }}
                hasMore={hasNextCategories}
                isLoadingMore={isFetchingNextCategories}
              />
            </div>

            {/* Dynamic Skills List */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                  Skills *
                </label>
                <button
                  type="button"
                  onClick={() =>
                    append({
                      skill: "",
                      proficiency: "intermediate",
                      experience_years: 0,
                    })
                  }
                  className="text-xs font-bold text-purple-600 dark:text-purple-400 hover:text-purple-700 flex items-center gap-1.5 transition-colors"
                >
                  <Plus className="w-4 h-4" /> Add Skill
                </button>
              </div>

              <div className="space-y-3">
                {fields.map((fieldItem, idx) => (
                  <div
                    key={fieldItem.id}
                    className="flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm"
                  >
                    {/* Dependent Skill SelectDropdown */}
                    <div className="flex-1 w-full sm:w-auto">
                      <SelectDropdown
                        control={control}
                        name={`skills.${idx}.skill`}
                        placeholder={
                          !selectedCategoryId
                            ? "Select Category first"
                            : isSkillsLoading
                              ? "Loading skills..."
                              : skillOptions.length === 0
                                ? "No skills available"
                                : "Select Skill"
                        }
                        options={skillOptions}
                        disabled={!selectedCategoryId || isSkillsLoading}
                        isSearchable
                        onSearch={(query) => setSkillSearch(query)}
                        onMenuScrollToBottom={() => {
                          if (hasNextSkills && !isFetchingNextSkills) {
                            fetchNextSkills();
                          }
                        }}
                        hasMore={hasNextSkills}
                        isLoadingMore={isFetchingNextSkills}
                      />
                    </div>

                    {/* Proficiency SelectDropdown */}
                    <div className="w-full sm:w-40">
                      <SelectDropdown
                        control={control}
                        name={`skills.${idx}.proficiency`}
                        options={PROFICIENCY_OPTIONS}
                        placeholder="Proficiency"
                      />
                    </div>

                    {/* Years of Experience */}
                    <div className="w-full sm:w-28">
                      <TextInput
                        control={control}
                        name={`skills.${idx}.experience_years`}
                        type="number"
                        placeholder="Years"
                        min={0}
                      />
                    </div>

                    {/* Remove Skill Button */}
                    <button
                      type="button"
                      onClick={() => handleRemoveSkill(idx)}
                      className="p-2 text-slate-400 hover:text-red-500 transition-colors self-center rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30"
                      title="Remove Skill"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}

                {fields.length === 0 && (
                  <div className="text-center p-8 text-slate-500 border border-dashed border-slate-200 dark:border-slate-800 rounded-xl bg-white dark:bg-slate-900">
                    No skills added to this category yet. Click "+ Add Skill"
                    above.
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="p-4 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3 bg-white">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                isSubmitting ||
                createMutation.isPending ||
                updateMutation.isPending
              }
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
            >
              {(isSubmitting ||
                createMutation.isPending ||
                updateMutation.isPending) && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileSkillFormModal;
